import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, Users, Wifi, WifiOff, Bell, BellOff, AtSign } from "lucide-react";

const ROLE_COLOR = {
  admin: "#ef4444",
  supervisor: "#f59e0b",
  dispatcher: "#3b82f6",
  subscriber: "#10b981",
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return ""; }
}

function initials(name) {
  if (!name) return "?";
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}

// Render text with @mentions highlighted. `myHandles` = lowercase set of strings
// matching the current user. Returns array of React fragments.
function renderWithMentions(text, myHandles, knownHandles) {
  if (!text) return null;
  const parts = text.split(/(@[A-Za-z0-9_.-]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith("@")) {
      const handle = p.slice(1).toLowerCase();
      const isMe = myHandles.has(handle);
      const isKnown = knownHandles.has(handle);
      if (isMe) {
        return (
          <span key={i} className="bg-amber-400/30 text-amber-200 font-bold px-1 rounded">
            {p}
          </span>
        );
      }
      if (isKnown) {
        return (
          <span key={i} className="text-primary font-semibold">
            {p}
          </span>
        );
      }
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// Browser desktop notification + an audio "ping" via the Web Audio API.
function useMentionAlert() {
  const ctxRef = useRef(null);
  const ensureCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctxRef.current = new AC();
    }
    return ctxRef.current;
  };
  return useCallback((title, body) => {
    // Beep — two-note chime
    try {
      const ctx = ensureCtx();
      if (ctx) {
        const now = ctx.currentTime;
        [880, 1320].forEach((freq, idx) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine";
          o.frequency.value = freq;
          g.gain.value = 0.0001;
          o.connect(g); g.connect(ctx.destination);
          const t0 = now + idx * 0.12;
          g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
          o.start(t0); o.stop(t0 + 0.22);
        });
      }
    } catch { /* ignore */ }
    // Desktop notification
    try {
      if ("Notification" in window && Notification.permission === "granted" && document.visibilityState !== "visible") {
        const n = new Notification(title, { body, tag: "psa-chat-mention", silent: false });
        setTimeout(() => { try { n.close(); } catch { /* ignore */ } }, 6000);
      }
    } catch { /* ignore */ }
  }, []);
}

export default function ChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const wsRef = useRef(null);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  const alertMention = useMentionAlert();
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Handles that count as "me" for mention detection (lowercase)
  const myHandles = useMemo(() => {
    const s = new Set();
    if (user?.username) s.add(String(user.username).toLowerCase());
    if (user?.email) s.add(String(user.email).split("@")[0].toLowerCase());
    if (user?.name) s.add(String(user.name).toLowerCase().replace(/\s+/g, ""));
    return s;
  }, [user]);

  // Known handles from currently-online users (helps highlight valid pings)
  const knownHandles = useMemo(() => {
    const s = new Set();
    online.forEach((u) => {
      if (u.username) s.add(String(u.username).toLowerCase());
      if (u.name) s.add(String(u.name).toLowerCase().replace(/\s+/g, ""));
    });
    return s;
  }, [online]);

  // Load recent history
  useEffect(() => {
    api.get("/chat/messages", { params: { limit: 100 } })
      .then((r) => setMessages(r.data || []))
      .catch(() => setMessages([]));
  }, []);

  // Open WebSocket
  useEffect(() => {
    if (!user) return;
    const url = BACKEND_URL.replace(/^http/, "ws") + "/api/chat/ws";
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "presence") setOnline(data.online || []);
        else if (data.type === "message") {
          const m = data.message;
          setMessages((prev) => [...prev, m]);
          // Ping me if mentioned and not authored by me
          if (m.user_id !== user.id) {
            const lowered = (m.text || "").toLowerCase();
            const mentioned = [...myHandles].some((h) => lowered.includes(`@${h}`));
            if (mentioned) alertMention(`${m.name} mentioned you`, m.text);
          }
        }
      } catch { /* ignore */ }
    };
    return () => { try { ws.close(); } catch { /* ignore */ } };
  }, [user, myHandles, alertMention]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ text: t }));
    setText("");
  };

  const insertMention = (handle) => {
    const ins = `@${handle} `;
    setText((cur) => (cur.endsWith(" ") || cur === "" ? cur + ins : cur + " " + ins));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const requestNotif = async () => {
    if (!("Notification" in window)) return;
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
    } catch { /* ignore */ }
  };

  const me = user?.id;
  const groupedOnline = useMemo(
    () => online.slice().sort((a, b) => (a.role || "").localeCompare(b.role || "")),
    [online]
  );

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="chat-page">
      {/* Top bar */}
      <div className="h-12 px-4 flex items-center justify-between border-b border-border bg-card/80 backdrop-blur shrink-0">
        <div className="flex items-center gap-3">
          <div className="font-mono text-xs tracking-widest text-primary">// DISPATCH CHAT ROOM</div>
          {connected
            ? <Badge variant="outline" className="border-green-500/40 text-green-500 font-mono text-[10px]"><Wifi className="w-3 h-3 mr-1" /> LIVE</Badge>
            : <Badge variant="outline" className="border-red-500/40 text-red-500 font-mono text-[10px]"><WifiOff className="w-3 h-3 mr-1" /> OFFLINE</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {notifPerm !== "granted" && "Notification" in window && (
            <Button size="sm" variant="outline" onClick={requestNotif} data-testid="enable-notif-btn"
              className="h-7 font-mono text-[10px] tracking-widest">
              <BellOff className="w-3 h-3 mr-1.5" /> ENABLE @ALERTS
            </Button>
          )}
          {notifPerm === "granted" && (
            <Badge variant="outline" className="border-green-500/40 text-green-500 font-mono text-[10px]" data-testid="notif-on">
              <Bell className="w-3 h-3 mr-1" /> @ALERTS ON
            </Badge>
          )}
          <Badge variant="outline" className="font-mono text-[10px]" data-testid="chat-online-count">
            <Users className="w-3 h-3 mr-1" /> {online.length} ONLINE
          </Badge>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-0 overflow-hidden">
        {/* Chat column */}
        <div className="flex flex-col overflow-hidden border-r border-border">
          <ScrollArea className="flex-1" data-testid="chat-scroll">
            <div ref={scrollerRef} className="px-4 py-3 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center text-muted-foreground font-mono text-xs py-12">
                  No messages yet. Be the first to say something.
                </div>
              )}
              {messages.map((m, idx) => {
                const mine = m.user_id === me;
                const color = ROLE_COLOR[m.role] || "#94a3b8";
                const lowered = (m.text || "").toLowerCase();
                const mentionsMe = !mine && [...myHandles].some((h) => lowered.includes(`@${h}`));
                return (
                  <div key={m.id || `m-${idx}`} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                      style={{ background: color }}
                      title={`${m.name} (${m.role})`}
                    >
                      {initials(m.name)}
                    </div>
                    <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5">
                        <span className="text-primary">{m.username || "—"}</span>
                        <span>·</span>
                        <span>{m.name}</span>
                        <span>·</span>
                        <span>{fmtTime(m.ts)}</span>
                      </div>
                      <div
                        data-testid={mentionsMe ? "chat-msg-mention-me" : undefined}
                        className={`mt-0.5 px-3 py-1.5 rounded-lg text-sm whitespace-pre-wrap break-words ${
                          mine
                            ? "bg-primary text-primary-foreground"
                            : mentionsMe
                              ? "bg-amber-500/10 border border-amber-400/50"
                              : "bg-card border border-border"
                        }`}
                      >
                        {renderWithMentions(m.text, myHandles, knownHandles)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Composer */}
          <form onSubmit={send} className="p-3 border-t border-border flex items-center gap-2 shrink-0" data-testid="chat-composer">
            <Input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={connected ? "Message the room… use @username to ping" : "Reconnecting…"}
              disabled={!connected}
              maxLength={500}
              data-testid="chat-input"
              className="font-mono"
              autoComplete="off"
            />
            <Button type="submit" disabled={!connected || !text.trim()} data-testid="chat-send" className="font-mono tracking-widest">
              <Send className="w-4 h-4 mr-1.5" /> SEND
            </Button>
          </form>
        </div>

        {/* Online sidebar */}
        <Card className="rounded-none border-0 h-full overflow-hidden flex flex-col">
          <CardHeader className="pb-2 shrink-0">
            <CardTitle className="text-sm font-mono tracking-widest flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" /> ONLINE ({online.length})
            </CardTitle>
            <CardDescription className="text-[10px]">Click a name to @mention them.</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              <div className="px-3 py-2 space-y-1.5" data-testid="online-list">
                {groupedOnline.length === 0 && (
                  <div className="text-xs text-muted-foreground py-4 text-center">No one online.</div>
                )}
                {groupedOnline.map((u) => {
                  const handle = u.username || (u.name || "").replace(/\s+/g, "").toLowerCase();
                  const isMe = u.user_id === me;
                  return (
                    <button
                      key={u.user_id}
                      onClick={() => !isMe && insertMention(handle)}
                      disabled={isMe}
                      data-testid={`online-user-${handle}`}
                      className={`w-full flex items-center gap-2 text-xs text-left rounded-md px-1.5 py-1 transition-colors ${
                        isMe ? "opacity-80" : "hover:bg-muted/50 cursor-pointer"
                      }`}
                      title={isMe ? "That's you" : `Mention @${handle}`}
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white relative shrink-0"
                        style={{ background: ROLE_COLOR[u.role] || "#94a3b8" }}
                      >
                        {initials(u.name)}
                        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border border-card" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{u.name}{isMe ? " (you)" : ""}</div>
                        <div className="font-mono text-[10px] text-muted-foreground truncate">
                          <span className="text-primary">{u.username || "—"}</span> · {u.role}
                        </div>
                      </div>
                      {!isMe && <AtSign className="w-3 h-3 text-muted-foreground shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
